import { Duration, Instant } from "@js-joda/core";
import {
  LabelLayer,
  ParentRect,
  ScadaFreeText,
  ScadaFreeTextTagTable,
  ScadaLabel,
  ScadaPolygon,
} from "components/dashboard/scada/scadaTypes";
import { create } from "zustand";

type Scada = {
  scadaId: string;
  labels: ScadaLabel[];
  labelLayers: LabelLayer[];
  polygons: ScadaPolygon[];
  freeTexts: ScadaFreeText[];
  freeTextTagTables?: ScadaFreeTextTagTable[];
};

type ScadaLabelsState = {
  scadaMap: Map<string, Scada>;
  scadaParentRectMap: Map<string, ParentRect>;
  lastClick: number;
  addScada: (scada: Scada) => void;
  updateScadaLabelLayers: (id: string, layers: LabelLayer[]) => void;
  updateScadaLabels: (id: string, labels: ScadaLabel[]) => void;
  updateScadaLabelXY: (id: string, identifier: string, x: number, y: number) => void;
  updatePolygons: (scadaId: string, polygons: ScadaPolygon[]) => void;
  updateFreeTexts: (scadaId: string, freeTexts: ScadaFreeText[]) => void;
  updateFreeTextTables: (scadaId: string, freeTextTagTables: ScadaFreeTextTagTable[]) => void;
  updateFreeTextTableXY: (scadaId: string, tableId: string, x: number, y: number) => void;
  setParentRect: (scadaId: string, rect: ParentRect) => void;
  setLastClick: (time: number) => void;
};

const useScadaStore = create<ScadaLabelsState>()(set => ({
  scadaMap: new Map<string, Scada>(),
  scadaParentRectMap: new Map<string, ParentRect>(),
  lastClick: Instant.now().minus(Duration.ofSeconds(1)).toEpochMilli(),

  addScada: newScada =>
    set(state => {
      const newMap = new Map(state.scadaMap);
      newMap.set(newScada.scadaId, newScada);
      return { ...state, scadaMap: newMap };
    }),

  setParentRect: (scadaId, rect) =>
    set(state => {
      const newMap = new Map(state.scadaParentRectMap);
      newMap.set(scadaId, rect);
      return { ...state, scadaParentRectMap: newMap };
    }),

  updateScadaLabelLayers: (id, layers) =>
    set(state => {
      const scada = state.scadaMap.get(id);
      if (!scada) return state;
      const newMap = new Map(state.scadaMap);
      newMap.set(id, { ...scada, labelLayers: layers });
      return { ...state, scadaMap: newMap };
    }),

  updateScadaLabels: (id, labels) =>
    set(state => {
      const scada = state.scadaMap.get(id);
      if (!scada) return state;
      const newMap = new Map(state.scadaMap);
      newMap.set(id, { ...scada, labels });
      return { ...state, scadaMap: newMap };
    }),

  updateScadaLabelXY: (id, identifier, x, y) =>
    set(state => {
      const scada = state.scadaMap.get(id);
      if (!scada) return state;
      const labels = scada.labels.map(l =>
        l.identifier === identifier ? { ...l, xCoord: x, yCoord: y } : l
      );
      const newMap = new Map(state.scadaMap);
      newMap.set(id, { ...scada, labels });
      return { ...state, scadaMap: newMap };
    }),

  updatePolygons: (scadaId, polygons) =>
    set(state => {
      const scada = state.scadaMap.get(scadaId);
      if (!scada) return state;
      const newMap = new Map(state.scadaMap);
      newMap.set(scadaId, { ...scada, polygons });
      return { ...state, scadaMap: newMap };
    }),

  updateFreeTexts: (scadaId, freeTexts) =>
    set(state => {
      const scada = state.scadaMap.get(scadaId);
      if (!scada) return state;
      const newMap = new Map(state.scadaMap);
      newMap.set(scadaId, { ...scada, freeTexts });
      return { ...state, scadaMap: newMap };
    }),

  updateFreeTextTables: (scadaId, freeTextTagTables) =>
    set(state => {
      const scada = state.scadaMap.get(scadaId);
      if (!scada) return state;
      const newMap = new Map(state.scadaMap);
      newMap.set(scadaId, { ...scada, freeTextTagTables });
      return { ...state, scadaMap: newMap };
    }),

  updateFreeTextTableXY: (scadaId, tableId, x, y) =>
    set(state => {
      const scada = state.scadaMap.get(scadaId);
      if (!scada) return state;

      const updatedTables = scada.freeTextTagTables?.map(t => (t.id === tableId ? { ...t, x, y } : t));

      const newMap = new Map(state.scadaMap);
      newMap.set(scadaId, { ...scada, freeTextTagTables: updatedTables });
      return { ...state, scadaMap: newMap };
    }),

  setLastClick: time => set(state => ({ ...state, lastClick: time })),
}));

export default useScadaStore;
